/**
 * Backtest Cookbook — curated, engine-validated snippet recipes.
 *
 * Every entry's `recipe` is proven to build + validate against the real graph
 * engine (see each cluster's co-located *.unit.test.ts). Recipes are the
 * canonical graph-engine form, so a curated entry and an AI-generated suggestion
 * are interchangeable. Design: docs/superpowers/specs/2026-06-18-backtest-cookbook-design.md
 */
export type { CookbookEntry } from './types'

import type { CookbookEntry } from './types'
import { momentumSeedRecipes } from './seed-recipes-momentum'
import { exitRiskSeedRecipes } from './seed-recipes-exit-risk'
import { indicatorsSeedRecipes } from './seed-recipes-indicators'
import { squeezeTrendSeedRecipes } from './seed-recipes-squeeze-trend'
import { structureSeedRecipes } from './seed-recipes-structure'
import { filtersSeedRecipes } from './seed-recipes-filters'
import { timingVolumeSeedRecipes } from './seed-recipes-timing-volume'

export {
  momentumSeedRecipes,
  exitRiskSeedRecipes,
  indicatorsSeedRecipes,
  squeezeTrendSeedRecipes,
  structureSeedRecipes,
  filtersSeedRecipes,
  timingVolumeSeedRecipes,
}

/**
 * Every seed cookbook entry, flattened across clusters, including the ones
 * the Trade Manager cannot express yet (`unavailable`). For tests and corpora
 * that cover the whole cookbook; lists people pick from use
 * `allCookbookRecipes`.
 */
export const everyCookbookRecipe: CookbookEntry[] = [
  ...momentumSeedRecipes,
  ...exitRiskSeedRecipes,
  ...indicatorsSeedRecipes,
  ...squeezeTrendSeedRecipes,
  ...structureSeedRecipes,
  ...filtersSeedRecipes,
  ...timingVolumeSeedRecipes,
]

/** The cookbook entries people can use: every entry not marked `unavailable`. */
export const allCookbookRecipes: CookbookEntry[] = everyCookbookRecipe.filter(
  (entry) => !entry.unavailable,
)

/** Entries grouped by their `category` cluster. */
export const cookbookRecipesByCategory: Record<string, CookbookEntry[]> = allCookbookRecipes.reduce(
  (acc, entry) => {
    ;(acc[entry.category] ??= []).push(entry)
    return acc
  },
  {} as Record<string, CookbookEntry[]>,
)
